import { useEffect, useMemo, useState } from "react"
import "../AppManagement.scss"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { addRequestedApp } from "../../../Redux/Slices/AppRequestSlice"
import { setUser } from "../../../Redux/Slices/UserSlice"
import {
  AutoComplete,
  Button,
  Card,
  Col,
  Input,
  message,
  Modal,
  Row,
  Select,
  Typography,
  Divider,
  Tag,
  Alert,
} from "antd"
import { SearchOutlined } from "@ant-design/icons"
import apiClient from "../../../Utils/apiClient"
import { useHeaderContext } from "../../../Utils/HeaderContext"
import PanelLayout from "../../../Layout/PanelLayout"
import EmptyState from "../../../components/EmptyState/EmptyState"
import StatusTag from "../../../components/StatusTag/StatusTag"
import TextArea from "antd/es/input/TextArea"
import { appStatusLabel, normalizeAppStatus, requestPackageId, samePackage } from "../../../Utils/appStatus"
import { inferSource } from "../../../Utils/wingetPackage"
import { deviceOs, isUnixDevice } from "../../../Utils/deviceOs"

const getStatusComponent = (status) => {
  const key = normalizeAppStatus(status)
  return (
    <StatusTag
      status={key === "pending" ? "Requested" : appStatusLabel(status)}
      className="w-100 text-center"
    />
  )
}

const toDeviceList = (userInfo) => {
  if (Array.isArray(userInfo?.deviceData)) return userInfo.deviceData.filter(Boolean)
  if (userInfo?.deviceData) return [userInfo.deviceData]
  return []
}

const idOf = (value) => {
  if (!value) return ""
  if (typeof value === "object") return String(value._id || value.id || "")
  return String(value)
}

const catalogText = (value) => {
  if (value == null) return ""
  if (typeof value === "string" || typeof value === "number") return String(value)
  if (typeof value === "object") {
    return String(value.Name || value.Version || value.version || value.Id || "")
  }
  return ""
}

const catalogItem = (raw, source) => {
  const latest = raw?.package && typeof raw.package === "object" ? raw.package : {}
  const PackageId = catalogText(
    raw.PackageId || raw.Id || raw.id || raw.packageId || latest.Id
  )
  const Title = catalogText(
    raw.Title || latest.Name || (typeof raw.package === "string" ? raw.package : "") || raw.name
  )
  return {
    PackageId,
    Title,
    source,
    version: catalogText(raw.version || latest.Version || latest.version),
    iconUrl: raw.iconUrl || raw.logo || raw.IconUrl || latest.IconUrl || "",
    DownloadCount: raw.DownloadCount || raw.downloadCount || 0,
  }
}

const canRetryRequest = (status) =>
  ["rejected", "failed", "uninstalled"].includes(normalizeAppStatus(status))

const osLabel = (device) => {
  const os = deviceOs(device)
  if (os === "linux") return "Linux"
  if (os === "macos") return "macOS"
  return "Windows"
}

const deviceOptionLabel = (device) => {
  if (!device) return ""
  const status = device.deviceStatus || "unknown"
  return `${device.hostname || "Unnamed device"} · ${osLabel(device)} · ${status}`
}

const UserAppInstallationRequest = () => {
  const { render } = useHeaderContext()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const userInfo = useSelector((state) => state?.user?.data)
  const devices = useMemo(() => toDeviceList(userInfo), [userInfo])
  const queryDeviceId = searchParams.get("deviceId") || searchParams.get("device") || ""
  const [deviceId, setDeviceId] = useState(
    idOf(
      queryDeviceId ||
        userInfo?.deviceIds?.[0] ||
        userInfo?.deviceId ||
        devices[0]?._id
    )
  )
  const selectedDevice =
    devices.find((item) => String(item._id) === String(deviceId)) || devices[0]
  const [requestDeviceIds, setRequestDeviceIds] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [selectedApps, setSelectedApps] = useState([])
  const [searchAppName, setSearchAppName] = useState("")
  const [reason, setReason] = useState("")
  const [isOpenModal, setisOpenModal] = useState(false)
  const [defaultData, setDefaultData] = useState([])
  const [requestedApps, setRequestedApps] = useState([])
  const [refresh, setRefresh] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [uninstallItem, setUninstallItem] = useState(null)
  const [uninstallReason, setUninstallReason] = useState("")
  const [catalogPolicy, setCatalogPolicy] = useState({ requiredIds: [], availableIds: [] })

  const requestDevices = useMemo(
    () =>
      devices.filter((item) =>
        requestDeviceIds.map(String).includes(String(item._id))
      ),
    [devices, requestDeviceIds]
  )

  const closeModal = () => {
    setisOpenModal(false)
    setSelectedApps([])
    setReason("")
    setRequestDeviceIds(
      selectedDevice?._id ? [String(selectedDevice._id)] : []
    )
  }

  const syncRequestDevices = (ids) => {
    const next = (Array.isArray(ids) ? ids : [ids])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
    setRequestDeviceIds(next.length ? next : selectedDevice?._id ? [String(selectedDevice._id)] : [])
  }

  useEffect(() => {
    if (queryDeviceId) {
      const match = devices.find((item) => String(item._id) === String(queryDeviceId))
      if (match) {
        setDeviceId(String(match._id))
        return
      }
    }
    if (!deviceId && devices[0]?._id) setDeviceId(idOf(devices[0]._id))
  }, [devices, deviceId, queryDeviceId])

  useEffect(() => {
    if (!userInfo?._id) return undefined
    const loadUser = async () => {
      try {
        const res = await apiClient(`/users/${userInfo._id}`)
        if (res?.success && res.user) {
          dispatch(
            setUser({
              ...userInfo,
              ...res.user,
              permissions: userInfo.permissions,
            })
          )
        }
      } catch (err) {
        console.error("Failed to load assigned devices", err)
      }
    }
    loadUser()
    return undefined
  }, [userInfo?._id])

  const load = async () => {
    if (!selectedDevice?._id) return
    try {
      const [packages, requests, policy] = await Promise.all([
        apiClient(`packages/by-user/${selectedDevice._id}`),
        apiClient(`request/getby-hostname/${encodeURIComponent(selectedDevice.hostname)}`),
        apiClient("request/catalog-policy").catch(() => null),
      ])
      if (policy?.result) {
        setCatalogPolicy({
          requiredIds: policy.result.requiredIds || [],
          availableIds: policy.result.availableIds || [],
        })
      }
      setDefaultData(
        Array.isArray(packages?.result?.defaultApps)
          ? packages.result.defaultApps
          : []
      )
      setRequestedApps(
        Array.isArray(requests?.result)
          ? requests.result
          : Array.isArray(packages?.result?.userRequestedApps)
          ? packages.result.userRequestedApps
          : []
      )
    } catch (err) {
      console.error("Failed to load app requests", err)
    }
  }

  useEffect(() => {
    load()
  }, [refresh, render, selectedDevice?._id, selectedDevice?.hostname])

  useEffect(() => {
    const live = (requestedApps || []).some((request) =>
      ["pending", "installing"].includes(normalizeAppStatus(request.status))
    )
    if (!live) return undefined
    const timer = setInterval(load, 15000)
    return () => clearInterval(timer)
  }, [requestedApps, selectedDevice?._id])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchAppName.trim()) findAppName()
      else {
        setSuggestions([])
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchAppName])

  const findAppName = async () => {
    const query = encodeURIComponent(searchAppName.trim())
    try {
      const [choco, winget] = await Promise.allSettled([
        apiClient(`choco-repo/search?q=${query}`, "GET"),
        apiClient(`winget/search?q=${query}`, "GET"),
      ])
      const chocoItems = (choco.value?.result || []).map((item) =>
        catalogItem(item, "choco")
      )
      const wingetItems = (winget.value?.result || []).map((item) =>
        catalogItem(item, "winget")
      )
      const merged = [...chocoItems, ...wingetItems].filter((item) => item.PackageId)
      setSuggestions(merged)
    } catch (err) {
      console.log("Error while searching app:", err)
      setSuggestions([])
    }
  }

  const matchingRequest = (app) =>
    (Array.isArray(requestedApps) ? requestedApps : []).find((request) =>
      samePackage(request, app)
    )

  const userRequestedApps = requestedApps.filter(
    (request) =>
      !defaultData.some((app) => samePackage(app, request))
  )

  const openRequestModal = (app) => {
    const existing = matchingRequest(app)
    if (devices.length <= 1 && existing) {
      const status = normalizeAppStatus(existing.status)
      if (status === "pending" || status === "installing") {
        message.info(
          `${app.Title || requestPackageId(app)} already has an open request on this device.`
        )
        return
      }
      if (status === "installed") {
        message.info(
          `${app.Title || requestPackageId(app)} is already installed on this device.`
        )
        return
      }
    }
    const next = {
      ...app,
      PackageId: requestPackageId(app),
      source: inferSource(app),
    }
    setSelectedApps([next])
    setRequestDeviceIds(
      selectedDevice?._id ? [String(selectedDevice._id)] : []
    )
    setisOpenModal(true)
  }

  const handleAppSelect = (app) => {
    if (!app?.PackageId) return
    openRequestModal(app)
    setSearchAppName("")
    setSuggestions([])
  }

  const handleRemoveApp = (appId) => {
    setSelectedApps((prev) => prev.filter((app) => app.PackageId !== appId))
  }

  const submitOne = async (app, justification, targetIds) => {
    const ids = (targetIds || [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
    if (!ids.length) {
      throw new Error("Choose at least one device for this request.")
    }
    const payload = {
      reason: justification,
      deviceId: ids[0],
      deviceIds: ids,
      user: {
        email: userInfo?.email,
        deviceId: ids[0],
        deviceIds: ids,
      },
      appData: {
        appName: app.Title || app.PackageId,
        packageId: app.PackageId,
        source: inferSource(app),
        version: app.version,
        iconUrl: app.iconUrl,
        DownloadCount: app.DownloadCount,
      },
    }
    const response = await apiClient("/request/install-app", "POST", payload)
    if (!response?.success) {
      throw new Error(response?.message || `Failed to request ${app.PackageId}`)
    }
    dispatch(addRequestedApp(app.PackageId))
    return response.message
  }

  const chosenRequestIds = () => {
    if (requestDeviceIds.length) return requestDeviceIds.map(String)
    if (selectedDevice?._id) return [String(selectedDevice._id)]
    return []
  }

  const handleSubmitRequest = async () => {
    const targets = chosenRequestIds()
    if (!targets.length) {
      message.warning("Choose at least one device for this request.")
      return
    }
    if (!reason.trim()) {
      message.warning("Please provide a business justification for this request.")
      return
    }
    if (!selectedApps.length) {
      message.warning("Select at least one application.")
      return
    }
    setSubmitting(true)
    try {
      const messages = []
      for (const app of selectedApps) {
        messages.push(await submitOne(app, reason.trim(), targets))
      }
      message.success(messages[0] || "Request sent to admin for approval")
      closeModal()
      setRefresh((flag) => !flag)
    } catch (err) {
      message.error(err.message || "Failed to submit request")
    } finally {
      setSubmitting(false)
    }
  }

  const submitUninstall = async () => {
    if (!uninstallItem?._id) return
    if (!uninstallReason.trim()) {
      message.warning("Please provide a reason for uninstall.")
      return
    }
    setSubmitting(true)
    try {
      const response = await apiClient("request/uninstall", "POST", {
        requestId: uninstallItem._id,
        appData: [{ requestId: uninstallItem._id }],
        hostname: selectedDevice?.hostname,
        userEmail: userInfo?.email,
        reason: uninstallReason.trim(),
      })
      if (!response?.success) {
        message.error(response?.message || "Failed to start uninstall")
        return
      }
      message.success(response.message)
      setUninstallItem(null)
      setUninstallReason("")
      setRefresh((flag) => !flag)
    } catch (err) {
      message.error(err.message || "Failed to start uninstall")
    } finally {
      setSubmitting(false)
    }
  }

  const handleAssignedRequest = async (app) => {
    if (!selectedDevice?._id) {
      message.warning("Select a device first.")
      return
    }
    if (devices.length > 1) {
      openRequestModal({
        ...app,
        PackageId: app.PackageId,
        Title: app.Title || app.PackageId,
        source: inferSource(app),
        version: app.version,
        iconUrl: app.iconUrl,
      })
      return
    }
    setSubmitting(true)
    try {
      const msg = await submitOne(
        {
          ...app,
          PackageId: app.PackageId,
          Title: app.PackageId,
          source: inferSource(app),
        },
        "",
        [String(selectedDevice._id)]
      )
      message.success(msg || "Request sent to admin for approval")
      setRefresh((flag) => !flag)
    } catch (err) {
      message.error(err.message || "Failed to send request")
    } finally {
      setSubmitting(false)
    }
  }

  const renderAppActionButton = (app) => {
    const request = matchingRequest(app)
    if (request && !canRetryRequest(request.status)) {
      return getStatusComponent(request.status)
    }
    return (
      <Button
        type="primary"
        className="w-100"
        loading={submitting}
        onClick={() => handleAssignedRequest(app)}
      >
        {request ? "Request again" : "Request"}
      </Button>
    )
  }

  return (
    <PanelLayout
      title="My app requests"
      subtitle="Track the apps you have requested and their approval status."
      actions={
        devices.length > 1 ? (
          <Select
            className="app-mgmt__search"
            value={String(selectedDevice?._id || "")}
            onChange={setDeviceId}
            options={devices.map((item) => ({
              value: String(item._id),
              label: deviceOptionLabel(item),
            }))}
          />
        ) : selectedDevice?.hostname ? (
          <Tag>{selectedDevice.hostname}</Tag>
        ) : null
      }
    >
      {devices.length > 1 ? (
        <div className="app-mgmt__section">
          <h2>Choose a device</h2>
          <p className="app-mgmt__muted mb-2">
            Browse requests for one device below. When you submit, you can send
            the same app request to one or more of your devices.
          </p>
          <div className="app-mgmt__device-picker">
            {devices.map((item) => {
              const active = String(item._id) === String(selectedDevice?._id)
              return (
                <button
                  type="button"
                  key={String(item._id)}
                  className={`app-mgmt__device-chip${active ? " is-active" : ""}`}
                  onClick={() => setDeviceId(String(item._id))}
                >
                  <strong>{item.hostname || "Unnamed device"}</strong>
                  <span>
                    {osLabel(item)} · {item.deviceStatus || "unknown"}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          devices.length > 1
            ? `Approved apps install on the device(s) you pick in the request form. You are browsing ${selectedDevice?.hostname || "a device"} right now.`
            : "Approved apps install on this device at the next agent check-in."
        }
      />
      {!selectedDevice?._id ? (
        <EmptyState
          title="No device assigned"
          description="Ask an admin to assign a device to your account before requesting apps."
        />
      ) : (
        <>
          {selectedDevice &&
          String(selectedDevice.deviceStatus || "").toLowerCase() !== "online" &&
          String(selectedDevice.deviceStatus || "").toLowerCase() !== "away" ? (
            <p className="app-mgmt__muted mb-3">
              {selectedDevice.hostname} is {selectedDevice.deviceStatus || "offline"}.
              You can still submit a request; install starts after approval when the
              device is online.
            </p>
          ) : null}
          <div className="app-mgmt__section">
            <h2>Search catalog</h2>
            <AutoComplete
              className="app-mgmt__search"
              style={{ width: "100%", maxWidth: 480, marginBottom: 12 }}
              value={searchAppName}
              options={suggestions.map((item) => ({
                value: `${item.source}:${item.PackageId}`,
                label: (
                  <div>
                    <div className="app-mgmt__name">{item.PackageId}</div>
                    <div className="app-mgmt__muted">
                      {item.source === "winget" ? "Winget" : "Chocolatey"}
                      {item.Title ? ` · ${item.Title}` : ""}
                      {item.version ? ` · ${item.version}` : ""}
                    </div>
                  </div>
                ),
                app: item,
              }))}
              onSelect={(_, option) => handleAppSelect(option?.app)}
              onSearch={setSearchAppName}
              filterOption={false}
              defaultActiveFirstOption={false}
              popupMatchSelectWidth={true}
            >
              <Input
                placeholder="Search Chocolatey or Winget"
                prefix={<SearchOutlined />}
                allowClear
              />
            </AutoComplete>
            {suggestions.length > 0 ? (
              <div className="app-mgmt__catalog">
                {suggestions.slice(0, 12).map((item) => (
                  <button
                    type="button"
                    key={`${item.source}:${item.PackageId}`}
                    className="app-mgmt__catalog-row"
                    onClick={() => handleAppSelect(item)}
                  >
                    <div>
                      <div className="app-mgmt__name">{item.PackageId}</div>
                      <div className="app-mgmt__muted">
                        {item.source === "winget" ? "Winget" : "Chocolatey"}
                        {item.Title ? ` · ${item.Title}` : ""}
                        {item.version ? ` · ${item.version}` : ""}
                      </div>
                    </div>
                    <span>Request</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="app-mgmt__section">
            <h2>Company portal</h2>
            {catalogPolicy.requiredIds?.length ? (
              <p className="app-mgmt__muted">
                Required: {catalogPolicy.requiredIds.join(", ")} (installed by IT)
              </p>
            ) : null}
            {catalogPolicy.availableIds?.length ? (
              <p className="app-mgmt__muted">
                Available to request: {catalogPolicy.availableIds.join(", ")}
              </p>
            ) : (
              <p className="app-mgmt__muted">
                Search the catalog to request an app. IT approves before install.
              </p>
            )}
            {defaultData?.length > 0 ? (
              <Row gutter={[16, 16]}>
                {defaultData.map((app) => (
                  <Col key={app.PackageId} xs={24} sm={12} md={8} lg={6}>
                    <Card className="app-mgmt__card" title={app.PackageId}>
                      {catalogPolicy.requiredIds?.some((id) => samePackage({ PackageId: id }, app)) ? (
                        <Tag color="red">Required</Tag>
                      ) : catalogPolicy.availableIds?.some((id) => samePackage({ PackageId: id }, app)) ? (
                        <Tag color="blue">Available</Tag>
                      ) : null}
                      {app.iconUrl ? (
                        <img alt="" src={app.iconUrl} className="app-mgmt__icon mb-2" />
                      ) : null}
                      <div className="app-mgmt__meta">
                        <span>Version {app.version || "—"}</span>
                        <span>{inferSource(app) === "winget" ? "Winget" : "Chocolatey"}</span>
                      </div>
                      {renderAppActionButton(app)}
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <EmptyState
                title="No assigned apps"
                description="Your device group does not have a default app bundle yet. Search the catalog to request an app."
              />
            )}
          </div>

          {userRequestedApps.length > 0 && (
            <div className="app-mgmt__section">
              <h2>Requested by you</h2>
              <Row gutter={[16, 16]}>
                {userRequestedApps.map((app) => (
                  <Col key={app._id || requestPackageId(app)} xs={24} sm={12} md={8} lg={6}>
                    <Card
                      className="app-mgmt__card"
                      title={app?.appData?.appName || requestPackageId(app)}
                    >
                      {app?.appData?.iconUrl ? (
                        <img
                          alt=""
                          src={app.appData.iconUrl}
                          className="app-mgmt__icon mb-2"
                        />
                      ) : null}
                      <div className="app-mgmt__meta">
                        <span>Version {app?.appData?.version || "—"}</span>
                        <span>
                          {inferSource(app.appData) === "winget" ? "Winget" : "Chocolatey"}
                        </span>
                      </div>
                      {app.lastError ? (
                        <p className="app-mgmt__muted mb-2">{app.lastError}</p>
                      ) : null}
                      {app.rejectReason ? (
                        <p className="app-mgmt__muted mb-2">Rejected: {app.rejectReason}</p>
                      ) : null}
                      {canRetryRequest(app.status) ? (
                        <Button
                          type="primary"
                          className="w-100"
                          loading={submitting}
                          onClick={() =>
                            openRequestModal({
                              PackageId: requestPackageId(app),
                              Title: app?.appData?.appName,
                              source: inferSource(app.appData),
                              version: app?.appData?.version,
                              iconUrl: app?.appData?.iconUrl,
                              DownloadCount: app?.appData?.DownloadCount,
                            })
                          }
                        >
                          Request again
                        </Button>
                      ) : normalizeAppStatus(app.status) === "installed" ? (
                        <Button
                          danger
                          className="w-100"
                          onClick={() => setUninstallItem(app)}
                        >
                          Request uninstall
                        </Button>
                      ) : (
                        getStatusComponent(app.status)
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </>
      )}

      <Modal
        open={isOpenModal}
        onCancel={closeModal}
        onOk={handleSubmitRequest}
        okText="Submit request"
        confirmLoading={submitting}
        cancelText="Cancel"
        title="Request application"
      >
        {devices.length > 1 ? (
          <>
            <Typography.Text strong>Devices for this request</Typography.Text>
            <Select
              mode="multiple"
              allowClear={false}
              placeholder="Select one or more of your devices"
              value={requestDeviceIds}
              onChange={syncRequestDevices}
              options={devices.map((item) => ({
                value: String(item._id),
                label: deviceOptionLabel(item),
              }))}
              style={{ width: "100%", margin: "8px 0 12px" }}
            />
            <Typography.Paragraph type="secondary">
              An admin must approve before install. Each selected device gets its
              own request.
            </Typography.Paragraph>
          </>
        ) : (
          <Typography.Paragraph type="secondary">
            This sends a request to an admin. The app is installed on{" "}
            {selectedDevice?.hostname || "your device"} only after the admin
            approves it.
          </Typography.Paragraph>
        )}
        {requestDevices.some((item) => isUnixDevice(item)) ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Linux and macOS devices install through the OS package manager. IT will map this catalog package if needed."
          />
        ) : null}
        <TextArea
          rows={4}
          placeholder="Business justification"
          maxLength={700}
          onChange={(e) => setReason(e.target.value)}
          value={reason}
        />
        <Divider />
        <Typography.Text strong>Selected applications</Typography.Text>
        <Row gutter={[16, 8]} className="mt-2">
          {selectedApps.map((app, index) => (
            <Col xs={24} key={`${app.PackageId}-${index}`}>
              <div className="app-mgmt__app" style={{ justifyContent: "space-between" }}>
                <div className="app-mgmt__app">
                  {app.iconUrl ? (
                    <img src={app.iconUrl} alt="" className="app-mgmt__icon" />
                  ) : null}
                  <div>
                    <div className="app-mgmt__name">{app.PackageId}</div>
                    <div className="app-mgmt__muted">
                      {inferSource(app) === "winget" ? "Winget" : "Chocolatey"}
                    </div>
                  </div>
                </div>
                <Button type="text" danger onClick={() => handleRemoveApp(app.PackageId)}>
                  Remove
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      </Modal>
      <Modal
        title="Request uninstall"
        open={Boolean(uninstallItem)}
        onOk={submitUninstall}
        confirmLoading={submitting}
        onCancel={() => {
          setUninstallItem(null)
          setUninstallReason("")
        }}
        okText="Uninstall"
        okButtonProps={{ danger: true }}
      >
        <p>Why uninstall {uninstallItem?.appData?.appName || requestPackageId(uninstallItem || {})}?</p>
        <TextArea
          rows={4}
          value={uninstallReason}
          onChange={(e) => setUninstallReason(e.target.value)}
          placeholder="Reason for uninstall"
        />
      </Modal>
    </PanelLayout>
  )
}

export default UserAppInstallationRequest
